import type { OverrideProperties, Simplify } from 'type-fest'
import { z } from 'zod'

import { EnumBuilder, type ZodSimplify } from '@mntn-dev/utility-types'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  DateSchema,
  NonEmptyStringSchema,
  UrlSchema,
} from './property.models.ts'
import { AgreementIdSchema } from './unique-id.models.ts'

export const [AgreementTypes, AgreementTypeSchema, AgreementTypeEnum] =
  EnumBuilder(
    'project-statement-of-work', // SOW terms to be accepted by Brand
    'project-greenlight-agreement', // GLA terms to be accepted by Maker
    'maker-project-payment-terms', // Payment terms to be accepted by Maker
    'client-terms', // QuickFrame Client Terms and Conditions
    'maker-terms' // QuickFrame Maker Terms and Conditions
  )
export type AgreementType = ZodSimplify<typeof AgreementTypeSchema>

export const AgreementVersionSchema = DateSchema

/**
 * AgreementDomainSelectModel
 */
export const AgreementDomainSelectModelSchema = z.object({
  agreementId: AgreementIdSchema,
  type: AgreementTypeSchema,
  version: AgreementVersionSchema,
  title: NonEmptyStringSchema(),
  link: UrlSchema,
})

export type AgreementDomainSelectModel<
  Type extends AgreementType = AgreementType,
> = OverrideProperties<
  ZodSimplify<typeof AgreementDomainSelectModelSchema>,
  { type: Type }
>

/**
 * AgreementDomainCreateModel
 */
export const AgreementDomainCreateModelSchema = z.never()

export type AgreementDomainCreateModel = ZodSimplify<
  typeof AgreementDomainCreateModelSchema
>

/**
 * AgreementDomainUpdateModel
 */
export const AgreementDomainUpdateModelSchema = z.never()

export type AgreementDomainUpdateModel = ZodSimplify<
  typeof AgreementDomainUpdateModelSchema
>

/**
 * AgreementDomainSelectModelFilters
 */
export const AgreementDomainSelectModelFiltersSchema = z
  .object({
    agreementId: ModelFilterSchema(AgreementIdSchema),
    type: ModelFilterSchema(AgreementTypeSchema),
  })
  .partial()

export type AgreementDomainSelectModelFilters = ZodSimplify<
  typeof AgreementDomainSelectModelFiltersSchema
>

export const ProjectAgreementSchema = AgreementDomainSelectModelSchema.pick({
  agreementId: true,
  version: true,
  type: true,
})

export type ProjectAgreement = ZodSimplify<typeof ProjectAgreementSchema>

export const ClientTermsAgreementSchema = ProjectAgreementSchema.extend({
  type: AgreementTypeSchema.extract(['client-terms']),
})

export type ClientTermsAgreement = ZodSimplify<
  typeof ClientTermsAgreementSchema
>

export const MakerTermsAgreementSchema = ProjectAgreementSchema.extend({
  type: AgreementTypeSchema.extract(['maker-terms']),
})

export type MakerTermsAgreement = ZodSimplify<typeof MakerTermsAgreementSchema>

export const MakerProjectPaymentTermsAgreementSchema =
  ProjectAgreementSchema.extend({
    type: AgreementTypeSchema.extract(['maker-project-payment-terms']),
  })

export type MakerProjectPaymentTermsAgreement = ZodSimplify<
  typeof MakerProjectPaymentTermsAgreementSchema
>

export const isAgreementType = <
  Agreement extends AgreementDomainSelectModel,
  Type extends AgreementType,
>(
  agreement: Agreement | undefined,
  type: Type
): agreement is Simplify<Agreement & { type: Type }> => {
  return agreement?.type === type
}
